import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '@/types/models';
import { api } from '@/lib/api';

interface AuthContextType {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  login: (newUser: User) => void;
  logout: () => void;
  isAuthenticated: boolean;
  hasSeenWalkthrough: boolean;
  markWalkthroughDone: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Global Authentication Context Provider.
 * Hydrates the session from localStorage on initial mount.
 * Exposes methods to log in, log out, and update the walkthrough flag.
 * All API requests will automatically use the token managed here via `APIClient`.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    if (typeof window !== 'undefined') {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          return JSON.parse(storedUser) as User;
        } catch {
          localStorage.removeItem('user');
          return null;
        }
      }
    }
    return null;
  });
  const [token, setToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // No initialization effect needed anymore as we use lazy useState
  useEffect(() => {
    // This effect can now be empty or removed if no other side effects are needed on mount
  }, []);

  const login = (newUser: User) => {
    localStorage.setItem('user', JSON.stringify(newUser));
    setUser(newUser);
    console.log('[Auth] Login successful');
  };

  const logout = async () => {
    localStorage.removeItem('user');
    setUser(null);
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } catch (e) {
      // ignore
    }
    window.location.href = '/login';
  };

  /**
   * Mark the onboarding walkthrough as completed/dismissed.
   * Persists the flag to the DB via PATCH /auth/walkthrough-complete
   * and updates both localStorage and in-memory state immediately so
   * the tour does not re-appear within the same session.
   */
  const markWalkthroughDone = () => {
    if (!user) return;

    const updatedUser: User = { ...user, hasSeenWalkthrough: true };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));

    // Persist to DB in the background (no-await by design — failure is non-critical)
    api.auth.completeWalkthrough().catch((err) => {
      console.warn('[Auth] Failed to persist walkthrough flag:', err);
    });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token: null,
        refreshToken: null,
        isLoading,
        login,
        logout,
        isAuthenticated: !!user,
        hasSeenWalkthrough: !!user?.hasSeenWalkthrough,
        markWalkthroughDone,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
